import type { TournamentResult } from '@brush/api-client';

/**
 * How an unclaimed place reads on a tournament's results — a finish the sheet records while nobody
 * has yet come forward as the person who made it. It has no player, not a placeholder one; see
 * `TournamentResult.PlayerId` on the server for why.
 */

/**
 * The name a result's row shows.
 *
 * An unclaimed place is named by its place and nothing else. The card the results file gave it is
 * provenance — a number the source generated because it could not name the finisher — and showing
 * it where a name goes would present it as the person's identity, which it never is. A player row
 * whose lookup missed still reads "Unknown player": that is a fault worth noticing, and an unclaimed
 * place is not one.
 */
export function standingName(result: TournamentResult, player: { name: string } | undefined): string {
  if (result.unclaimed) return `Unclaimed — place ${result.position}`;
  return player?.name ?? 'Unknown player';
}

/**
 * A stable key per row. A player row is keyed by its player, who holds at most one place in a
 * tournament; an unclaimed place by its place, which is required for one and unique in the sheet.
 * Keyed by the null player instead, every unclaimed row would share one key.
 */
export function resultRowKey(result: TournamentResult): string {
  return result.playerId ?? `unclaimed-${result.position}`;
}
